<script setup>
// Настройки LLM (Р9.2): несколько подключений провайдеров (по ключу на
// провайдера), адрес API подставляется автоматически, «Свой провайдер»
// принимает ручной адрес. Модель аккаунта не выбирается — модели задаются
// на агентах/ассистентах, эмбеддинг-модели — на базах знаний.
import { computed, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAssistantSettingsStore } from 'dashboard/store/assistant/settings';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';

const { t } = useI18n();
const settingsStore = useAssistantSettingsStore();

const state = reactive({
  dailyBudgetRub: '',
  // Р11 п.8 / Р46: единая пара «провайдер + модель» эмбеддингов для всех баз
  // знаний и RAG аккаунта.
  embeddingProvider: '',
  embeddingModel: '',
});

const credentialForm = reactive({
  provider: '',
  apiKey: '',
  baseUrl: '',
  isSaving: false,
});

// Р10 п.11: выбор методов подтверждения телефона убран из UI — функционал
// будет позже; бэкенд-цепочка (звонок → SMS) остаётся под капотом.
const isFetching = computed(() => settingsStore.uiFlags.isFetching);
const isUpdating = computed(() => settingsStore.uiFlags.isUpdating);
const isCheckingHealth = computed(() => settingsStore.uiFlags.isCheckingHealth);

const health = computed(() => settingsStore.health);

// Формулировки провайдеров разные и всегда английские — показываем владельцу
// причину на его языке, а сырой текст оставляем только для неизвестных случаев.
const healthRows = computed(() => {
  if (!health.value) return [];
  return ['chat', 'embeddings'].map(key => {
    const result = health.value[key] || {};
    const status = result.status || 'failed';
    const known = [
      'ok',
      'no_money',
      'bad_key',
      'no_model',
      'rate_limited',
      'unreachable',
      'not_configured',
    ].includes(status);
    return {
      key,
      ok: status === 'ok',
      label: t(`ASSISTANT.SETTINGS.HEALTH.${key.toUpperCase()}`),
      verdict: known
        ? t(`ASSISTANT.SETTINGS.HEALTH.STATUS.${status.toUpperCase()}`)
        : result.detail || t('ASSISTANT.SETTINGS.HEALTH.STATUS.FAILED'),
    };
  });
});

const runHealthCheck = async () => {
  try {
    await settingsStore.healthCheck();
  } catch (error) {
    useAlert(t('ASSISTANT.SETTINGS.HEALTH.ERROR'));
  }
};

const credentials = computed(() => settingsStore.credentials || []);

const providerMeta = provider =>
  (settingsStore.providers || []).find(item => item.value === provider) || {};

const providerLabel = provider => providerMeta(provider).label || provider;

// В форму добавления попадают только ещё не подключённые провайдеры.
const availableProviders = computed(() => {
  const connected = new Set(credentials.value.map(item => item.provider));
  return (settingsStore.providers || []).filter(
    item => !connected.has(item.value)
  );
});

const selectedProviderMeta = computed(() =>
  providerMeta(credentialForm.provider)
);

const needsBaseUrl = computed(
  () => selectedProviderMeta.value.custom_base_url === true
);

// Р46: адрес обязателен только когда у провайдера нет дефолта (свой шлюз);
// у Qwen поле опционально — пустое значение берёт адрес из каталога.
const baseUrlRequired = computed(
  () => needsBaseUrl.value && !selectedProviderMeta.value.base_url
);

// Р46: провайдеры эмбеддингов = ПОДКЛЮЧЁННЫЕ (есть ключ) с embeddings: true.
// Модели фильтруются по выбранному провайдеру; у «своего» каталога нет —
// модель вводится вручную (должна выдавать 1536-мерные векторы).
const embeddingProviders = computed(() => {
  const connected = new Set(credentials.value.map(item => item.provider));
  return (settingsStore.providers || []).filter(
    item => item.embeddings && connected.has(item.value)
  );
});

const embeddingModelOptions = computed(
  () => (settingsStore.embeddingModels || {})[state.embeddingProvider] || []
);

// Р67: список моделей живой (тянется по API провайдера), поэтому в нём
// попадаются модели на 1024/3072/4096 измерений — колонка рассчитана на 1536,
// такие выбрать нельзя. compatible: true — годится, false — нет,
// null — размерность ещё уточняется фоновой пробой.
const REQUIRED_DIMENSIONS = 1536;

const embeddingModelLabel = model => {
  const name = model.display_name || model.id;
  if (model.compatible === false) {
    return t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.INCOMPATIBLE', {
      name,
      dimensions: model.dimensions,
    });
  }
  return name;
};

const hasIncompatibleModels = computed(() =>
  embeddingModelOptions.value.some(model => model.compatible === false)
);

const isFreeformEmbeddingModel = computed(
  () =>
    state.embeddingProvider &&
    embeddingModelOptions.value.length === 0 &&
    providerMeta(state.embeddingProvider).custom_base_url === true
);

// Подставляем первую ПРИГОДНУЮ модель (в живом каталоге первой по алфавиту
// запросто окажется модель с чужой размерностью).
const firstUsableEmbeddingModel = () => {
  const options = embeddingModelOptions.value;
  return (options.find(m => m.compatible !== false) || options[0])?.id || '';
};

const onEmbeddingProviderChange = () => {
  const options = embeddingModelOptions.value;
  if (options.length && !options.some(m => m.id === state.embeddingModel)) {
    state.embeddingModel = firstUsableEmbeddingModel();
  }
};

// Р46: если сохранённый/дефолтный провайдер эмбеддингов не подключён (нет
// ключа), селект показал бы пустое значение с чужими моделями. Подставляем
// первый доступный подключённый провайдер (и его модель). Срабатывает и при
// удалении ключа выбранного провайдера.
const reconcileEmbeddingProvider = () => {
  const options = embeddingProviders.value;
  if (!options.length) return;
  if (!options.some(p => p.value === state.embeddingProvider)) {
    state.embeddingProvider = options[0].value;
    onEmbeddingProviderChange();
  }
};

watch(embeddingProviders, reconcileEmbeddingProvider);

// Предупреждение о переиндексации — только при реальной смене пары.
const embeddingChanged = computed(
  () =>
    (settingsStore.embeddingProvider || 'openai') !==
      (state.embeddingProvider || 'openai') ||
    (settingsStore.embeddingModel || '') !== (state.embeddingModel || '')
);

const addCredential = async () => {
  if (!credentialForm.provider || !credentialForm.apiKey) return;
  credentialForm.isSaving = true;
  try {
    await settingsStore.addCredential({
      provider: credentialForm.provider,
      api_key: credentialForm.apiKey,
      base_url: credentialForm.baseUrl.trim() || null,
    });
    credentialForm.provider = '';
    credentialForm.apiKey = '';
    credentialForm.baseUrl = '';
    useAlert(t('ASSISTANT.SETTINGS.CREDENTIALS.ADD_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('ASSISTANT.SETTINGS.CREDENTIALS.ADD_ERROR'));
  } finally {
    credentialForm.isSaving = false;
  }
};

const removeCredential = async credential => {
  try {
    await settingsStore.removeCredential(credential.id);
    useAlert(t('ASSISTANT.SETTINGS.CREDENTIALS.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('ASSISTANT.SETTINGS.CREDENTIALS.REMOVE_ERROR')
    );
  }
};

// Р11 п.7: каталог моделей подтягивается автоматически (кнопки «Обновить»
// нет) — при загрузке страницы фоново синкаем каждое подключение по API
// провайдера, список всегда актуален.
// Р67: синк обновляет и каталог эмбеддинг-моделей — после него перечитываем
// справочники, чтобы в выпадающем списке был свежий список провайдера.
const refreshAllModels = async () => {
  const jobs = (settingsStore.credentials || []).map(credential =>
    settingsStore.syncCredentialModels(credential.id).catch(() => null)
  );
  if (!jobs.length) return;
  await Promise.all(jobs);
  await settingsStore.refreshCatalogs().catch(() => null);
  reconcileEmbeddingProvider();
};

const syncStateFromStore = () => {
  state.dailyBudgetRub = settingsStore.dailyBudgetRub ?? '';
  state.embeddingProvider = settingsStore.embeddingProvider || 'openai';
  state.embeddingModel = settingsStore.embeddingModel || '';
  reconcileEmbeddingProvider();
};

const handleSubmit = async () => {
  try {
    await settingsStore.update({
      config: {
        daily_budget_rub:
          state.dailyBudgetRub === '' || state.dailyBudgetRub === null
            ? ''
            : Number(state.dailyBudgetRub),
        embedding_provider: state.embeddingProvider,
        embedding_model: state.embeddingModel,
      },
    });
    syncStateFromStore();
    useAlert(t('ASSISTANT.SETTINGS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    // Р67: бэкенд отдаёт понятную причину отказа (например, модель выдаёт
    // не 1536 измерений) — показываем её, а не «422».
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('ASSISTANT.SETTINGS.API.ERROR_MESSAGE')
    );
  }
};

onMounted(async () => {
  await settingsStore.fetch();
  syncStateFromStore();
  refreshAllModels();
});
</script>

<template>
  <PageLayout
    :header-title="$t('ASSISTANT.SETTINGS.HEADER')"
    :description="$t('ASSISTANT.SETTINGS.DESCRIPTION')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
  >
    <template #body>
      <div class="flex flex-col gap-6 max-w-xl">
        <!-- Подключения провайдеров -->
        <fieldset
          class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak"
        >
          <legend class="px-1 text-sm font-medium text-n-slate-12">
            {{ t('ASSISTANT.SETTINGS.CREDENTIALS.TITLE') }}
          </legend>
          <p class="text-xs text-n-slate-11">
            {{ t('ASSISTANT.SETTINGS.CREDENTIALS.HELP_TEXT') }}
          </p>

          <div v-if="!credentials.length" class="py-2 text-sm text-n-slate-11">
            {{ t('ASSISTANT.SETTINGS.CREDENTIALS.EMPTY') }}
          </div>

          <div
            v-for="credential in credentials"
            :key="credential.id"
            class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-n-alpha-1"
          >
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ providerLabel(credential.provider) }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{
                  t('ASSISTANT.SETTINGS.CREDENTIALS.KEY_HINT', {
                    hint: credential.api_key_hint || '····',
                  })
                }}
                <template v-if="credential.resolved_base_url">
                  · {{ credential.resolved_base_url }}
                </template>
                ·
                {{
                  t('ASSISTANT.SETTINGS.CREDENTIALS.MODELS_COUNT', {
                    count: (credential.models || []).length,
                  })
                }}
              </span>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <!-- Р11 п.7: список моделей обновляется автоматически при
                   загрузке; ручной кнопки «Обновить модели» больше нет. -->
              <Button
                type="button"
                icon="i-lucide-trash-2"
                size="sm"
                variant="ghost"
                color="ruby"
                @click="removeCredential(credential)"
              />
            </div>
          </div>

          <!-- Добавление подключения -->
          <div
            v-if="availableProviders.length"
            class="flex flex-col gap-2 pt-2 border-t border-n-weak"
          >
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('ASSISTANT.SETTINGS.CREDENTIALS.PROVIDER_LABEL') }}
              </span>
              <select
                v-model="credentialForm.provider"
                class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
              >
                <option value="" disabled>
                  {{ t('ASSISTANT.SETTINGS.CREDENTIALS.PROVIDER_PLACEHOLDER') }}
                </option>
                <option
                  v-for="provider in availableProviders"
                  :key="provider.value"
                  :value="provider.value"
                >
                  {{ provider.label }}
                </option>
              </select>
            </label>

            <Input
              v-model="credentialForm.apiKey"
              type="password"
              autocomplete="new-password"
              :label="t('ASSISTANT.SETTINGS.FORM.API_KEY.LABEL')"
              :placeholder="t('ASSISTANT.SETTINGS.FORM.API_KEY.PLACEHOLDER')"
            />

            <Input
              v-if="needsBaseUrl"
              v-model="credentialForm.baseUrl"
              :label="t('ASSISTANT.SETTINGS.CREDENTIALS.BASE_URL_LABEL')"
              :placeholder="
                selectedProviderMeta.base_url || 'https://llm.example.com/v1'
              "
              :message="
                baseUrlRequired
                  ? t('ASSISTANT.SETTINGS.CREDENTIALS.BASE_URL_HELP')
                  : t('ASSISTANT.SETTINGS.CREDENTIALS.BASE_URL_OPTIONAL', {
                      url: selectedProviderMeta.base_url,
                    })
              "
            />
            <p
              v-else-if="credentialForm.provider"
              class="text-xs text-n-slate-11"
            >
              {{
                t('ASSISTANT.SETTINGS.CREDENTIALS.BASE_URL_AUTO', {
                  url: selectedProviderMeta.base_url || '—',
                })
              }}
            </p>

            <div class="flex justify-end">
              <Button
                type="button"
                :label="t('ASSISTANT.SETTINGS.CREDENTIALS.ADD_BUTTON')"
                icon="i-lucide-plus"
                size="sm"
                :is-loading="credentialForm.isSaving"
                :disabled="
                  !credentialForm.provider ||
                  !credentialForm.apiKey ||
                  (baseUrlRequired && !credentialForm.baseUrl.trim())
                "
                @click="addCredential"
              />
            </div>
          </div>
        </fieldset>

        <!-- Общие настройки -->
        <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
          <!-- Р46: провайдер + модель эмбеддингов (только подключённые
               провайдеры с поддержкой эмбеддингов). -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ASSISTANT.SETTINGS.FORM.EMBEDDING_PROVIDER.LABEL') }}
            </span>
            <select
              v-model="state.embeddingProvider"
              class="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
              @change="onEmbeddingProviderChange"
            >
              <option
                v-for="provider in embeddingProviders"
                :key="provider.value"
                :value="provider.value"
              >
                {{ provider.label }}
              </option>
            </select>
            <span
              v-if="!embeddingProviders.length"
              class="text-xs text-n-slate-11"
            >
              {{ t('ASSISTANT.SETTINGS.FORM.EMBEDDING_PROVIDER.EMPTY') }}
            </span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.LABEL') }}
            </span>
            <Input
              v-if="isFreeformEmbeddingModel"
              v-model="state.embeddingModel"
              :placeholder="
                t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.CUSTOM_PLACEHOLDER')
              "
              class="max-w-sm"
            />
            <select
              v-else
              v-model="state.embeddingModel"
              class="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            >
              <option
                v-for="m in embeddingModelOptions"
                :key="m.id"
                :value="m.id"
                :disabled="m.compatible === false"
              >
                {{ embeddingModelLabel(m) }}
              </option>
            </select>
            <span class="text-xs text-n-slate-11">
              {{ t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.HELP_TEXT') }}
            </span>
            <!-- Р67: список живой, в нём есть модели с чужой размерностью —
                 объясняем, почему часть вариантов недоступна. -->
            <span v-if="hasIncompatibleModels" class="text-xs text-n-slate-11">
              {{
                t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.DIMENSIONS_NOTE', {
                  dimensions: REQUIRED_DIMENSIONS,
                })
              }}
            </span>
            <span v-if="embeddingChanged" class="text-xs text-n-amber-11">
              {{ t('ASSISTANT.SETTINGS.FORM.EMBEDDING_MODEL.REINDEX_WARNING') }}
            </span>
          </label>

          <Input
            v-model="state.dailyBudgetRub"
            type="number"
            min="0"
            step="0.01"
            :label="t('ASSISTANT.SETTINGS.FORM.BUDGET.LABEL')"
            :placeholder="t('ASSISTANT.SETTINGS.FORM.BUDGET.PLACEHOLDER')"
            :message="t('ASSISTANT.SETTINGS.FORM.BUDGET.HELP_TEXT')"
          />

          <!-- Р10 п.11: блок «Методы подтверждения телефона» убран из UI —
               функционал будет позже, выбор пользователю не показываем. -->
          <!-- Р78: провайдер отказывает молча — агент перестаёт отвечать, база
               знаний теряет векторы, а в настройках всё выглядит целым. Кнопка
               проверяет оба пути живьём и называет причину словами. -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between w-full gap-3">
              <Button
                type="button"
                variant="faded"
                color="slate"
                :label="t('ASSISTANT.SETTINGS.HEALTH.BUTTON')"
                :is-loading="isCheckingHealth"
                :disabled="isCheckingHealth"
                @click="runHealthCheck"
              />
              <Button
                type="submit"
                :label="t('ASSISTANT.SETTINGS.FORM.SUBMIT')"
                :is-loading="isUpdating"
                :disabled="isUpdating"
              />
            </div>
            <div v-if="health" class="flex flex-col gap-1 text-sm">
              <div
                v-for="row in healthRows"
                :key="row.key"
                class="flex items-start gap-2"
              >
                <span :class="row.ok ? 'text-n-teal-11' : 'text-n-ruby-11'">
                  {{ row.ok ? '✓' : '✕' }}
                </span>
                <span class="text-n-slate-11">
                  {{ row.label }} — {{ row.verdict }}
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>
  </PageLayout>
</template>
